import React, { useEffect, useState, useMemo } from 'react';
import { ArrowBack, Battery80, Add } from '@mui/icons-material';
import './PatientChart.css';
import { addIcon, avatarIcon, backArrow, batteryIcon, bpIcon, calenderIcon, emailIcon, glucoseIcon, heartRateIcon, phoneIcon, rrIcon, spo2Icon, stepIcon, watchIcon } from '../../../assets/Icons';

import Threshold from './TabContent/Threshold/Threshold';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getPatientList } from '../../../redux/reducers/patientReducer';
import { Grid2, IconButton, Typography } from '@mui/material';
import withNavigate from '../../../hocs/withNavigate';
import Loader from "../../../components/Loader";
import DownloadAGP from '../../DownloadAGP';

function getPatientStatus(vitalData, patientId) {
  if (!vitalData || typeof vitalData !== 'object') return null;
  for (const [status, patients] of Object.entries(vitalData)) {
    if (patients.includes(parseInt(patientId))) {
      return status;
    }
  }
  return null;
}

const VitalSign = ({ icon, value, unit, label, status, navigate, patient }) => (
  <div className={`vital-sign sign-status ${status?.toLowerCase().replace(' ', '-')}`}>
    <div className="vital-icon w-100">
      {icon}
      {label === 'BLOOD GLUCOSE' &&
        <div className="download-agp-button d-flex justify-content-end">
          <DownloadAGP patientId={patient?.userCredId} />
        </div>
      }
    </div>
    <div className="vital-unit">{unit}</div>
    <div className="vital-value">{value}</div>
    <div className="vital-label">{label}</div>
    {status && <div className={`text-capitalize ${status.toLowerCase().replace(' ', '-')}`}>{status}</div>}
  </div>
);

const PatientChart = ({ navigate }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { patientId } = location.state || {};

  const { patientList = [], patientPopulationsVitals = {}, loading } = useSelector((state) => state?.patient);
  const [activeTab, setActiveTab] = useState('LIVE VIEW');
  const [patient, setPatient] = useState({});

  // Memoize patient to avoid unnecessary re-renders
  const memoizedPatient = useMemo(() => patient, [patient]);

  useEffect(() => {
    dispatch(getPatientList());
  }, [dispatch]);

  useEffect(() => {
    const patientDetails = patientList.find(ele => ele.patientId == patientId)
    setPatient(patientDetails)
  }, [patient, patientList]);

  const tabs = [
    'LIVE VIEW',
    'HISTORICAL',
    'ALERTS',
    'NOTES',
    'MEDICAL REPORTS',
    'THRESHOLDS'
  ];

  const renderTabContent = (patient) => {
    const vitalsData = patient?.lastDeviceData ? JSON.parse(patient.lastDeviceData) : {}
    switch (activeTab) {
      case 'LIVE VIEW':
        return (
          <>
            <div className="vitals-grid">
              <VitalSign
                icon={<img src={glucoseIcon} alt="Blood Glucose" />}
                unit="mg/dL"
                value={vitalsData?.Glucose}
                label="BLOOD GLUCOSE"
                status={getPatientStatus(patientPopulationsVitals?.BloodGlucose, patientId)}
                patient={patient}
              />
              <VitalSign
                icon={<img src={heartRateIcon} alt="Heart Rate" />}
                unit="bpm"
                value={vitalsData?.HR}
                label="HEART RATE"
                status={getPatientStatus(patientPopulationsVitals?.HeartRate, patientId)}
              />
              <VitalSign
                icon={<img src={bpIcon} alt="Blood Pressure" />}
                unit="mmHg"
                value={`${vitalsData?.SBP || 0}/${vitalsData?.DBP || 0}`}
                label="BLOOD PRESSURE"
                status={getPatientStatus(patientPopulationsVitals?.DBP, patientId)}
              />
              <VitalSign
                icon={<img src={rrIcon} alt="Respiration Rate" />}
                unit="brpm"
                value={vitalsData?.RR}
                label="RESPIRATION RATE"
                status={getPatientStatus(patientPopulationsVitals?.RespirationRate, patientId)}
              />
              <VitalSign
                icon={<img src={spo2Icon} alt="Oxygen Saturation" />}
                unit="Percentage"
                value={vitalsData.SPO2}
                label="OXYGEN SATURATION"
                status={getPatientStatus(patientPopulationsVitals?.SPO2, patientId)}
              />
              {/* <VitalSign
                icon={<img src={stepIcon} alt="Steps Walked" />}
                unit="Steps"
                value="5500"
                label="STEPS WALKED"
              /> */}
            </div>
          </>
        );
      case 'THRESHOLDS':
        return <Threshold patient={memoizedPatient} />;
      default:
        return <h3>{activeTab}</h3>;
    }
  };

  return (
    <div className="patient-chart">
      <Grid2
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          <IconButton sx={{ color: "black" }} onClick={() => navigate('/patient-population')}>
            <img src={backArrow} alt="Back" width={24} height={24} />
          </IconButton>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem", md: "2rem" } }}
          >
            <p className="text-capitalize">Patient chart</p>
          </Typography>
        </Grid2>

      <div className="patient-info">
        <div className="patient-info-content">
          <div className="patient-avatar">
            {/* <img src="/avatar-placeholder.png" alt="Patient" /> */}
          </div>
          <div className="patient-details">
            <div className="name-section">
              <h2>{patient?.patientFirstName} {patient?.patientLastName}</h2>
            </div>
            <div className="info-section">
              <div className="info-column">
                <div className="info-item ms-1">
                  <img src={calenderIcon} alt="Calendar" />
                  <span>{moment(patient?.birthDay).format('MMM Do YY')}</span>
                </div>
                <div className="device-info">
                  <img src={watchIcon} alt="Device" className="device-icon" />
                  <span>LifeLeaf LPW2</span>
                </div>
              </div>
              <div className="info-column">
                <div className="info-item">
                  <img src={avatarIcon} alt="User" />
                  <span>{patient?.gender}</span>
                </div>
                <div className="device-info">
                  <span><b>Serial:</b> {patient?.serialNumber}</span>
                </div>
              </div>
              <div className="info-column ms-4">
                <div className="info-item">
                  <img src={emailIcon} alt="Email" />
                  <span>{patient?.email}</span>
                </div>
                {/* <div className="battery-info">
                  <div className="battery-row">
                    <img src={batteryIcon} alt="battery" />
                    Battery: 80%
                  </div>
                  <span className="status connected">Connected</span>
                </div> */}
              </div>
              <div className="info-column">
                <div className="info-item">
                  <img src={phoneIcon} alt="Phone" />
                  <span>{patient?.phone}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tabs">
          {tabs.map(tab => (
            <button
              key={tab}
              className={`tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className='active-tab-label ps-2 pt-2 pb-1'><h3>{activeTab}</h3></div>
        <div className="tab-content">
          {renderTabContent(patient)}
        </div>
      </div>
      <Loader loading={loading} />
    </div>
  );
};

export default withNavigate(PatientChart); 